import { useEffect, useState } from 'react';
import { efemService } from '../services/EfemService';
import {
  countGatewaysMatchingTemplate,
  type ConfiguredWaferCameraRole,
} from '../utils/waferImagingTemplateFilters';

export interface GatewayCameraRolesSnapshot {
  gatewayCount: number;
  /** One camera role list per gateway (only gateways with a serial). */
  rolesByGateway: ConfiguredWaferCameraRole[][];
}

export function useGatewayCameraRolesSnapshot(): {
  snapshot: GatewayCameraRolesSnapshot | null;
  loading: boolean;
} {
  const [snapshot, setSnapshot] = useState<GatewayCameraRolesSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      setLoading(true);
      try {
        const { clients } = await efemService.getEfemClients();
        const serials = clients
          .map((c) => c.gatewaySerialNumber)
          .filter((sn): sn is string => Boolean(sn));

        const rolesByGateway = await Promise.all(
          serials.map(async (sn) => {
            try {
              const matrix = await efemService.getEfemPeripheralMatrix(sn);
              return matrix.waferCameras
                .filter(
                  (slot) =>
                    slot.configured && slot.supportedWaferSize != null && slot.idSide != null,
                )
                .map((slot) => ({
                  supportedWaferSize: slot.supportedWaferSize as string,
                  idSide: slot.idSide as string,
                }));
            } catch {
              return [];
            }
          }),
        );

        if (!cancelled) {
          setSnapshot({
            gatewayCount: serials.length,
            rolesByGateway,
          });
        }
      } catch {
        if (!cancelled) {
          setSnapshot(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { snapshot, loading };
}

export function formatGatewayCompatibilityLabel(
  template: { waferSize: string; idSide: string },
  snapshot: GatewayCameraRolesSnapshot | null,
): string {
  if (!snapshot) {
    return '—';
  }
  const matchCount = countGatewaysMatchingTemplate(template, snapshot.rolesByGateway);
  if (snapshot.gatewayCount === 0) {
    return 'No gateways';
  }
  if (matchCount === 0) {
    return 'No matching camera';
  }
  return `${matchCount} of ${snapshot.gatewayCount} gateway${snapshot.gatewayCount === 1 ? '' : 's'}`;
}
